// frontend/src/App.jsx
// Root application shell — routing, nav, auth gate
// Module 8: self-registration, invite accept, upgrade/trial wall, user management
// GTM: virtual page view tracking on client-side route change, with hashed
// user identifier for authenticated vs anonymous segmentation
// commit: fix(routing): remove nested BrowserRouter, use shared Routes for trial-expired branch
// commit: feat(gtm): tag virtual_page_view with hashed user id for authenticated sessions

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  getMe,
  getNotifications,
  getNotificationCount,
  dismissNotification,
  markAllNotificationsRead,
  getRegulatoryAlerts,
} from './api';
import Login             from './pages/Login';
import Register          from './pages/Register';
import InviteAccept      from './pages/InviteAccept';
import Upgrade           from './pages/Upgrade';
import Dashboard         from './pages/Dashboard';
import Vendors           from './pages/Vendors';
import Controls          from './pages/Controls';
import Incidents         from './pages/Incidents';
import BoardPacks        from './pages/BoardPacks';
import RegulatoryLibrary from './pages/RegulatoryLibrary';
import RegulatoryAlerts  from './pages/RegulatoryAlerts';
import UserManagement    from './pages/UserManagement';
import Profile           from './pages/Profile';

// ---------------------------------------------------------------------------
// GTM: one-way hash of a user identifier, never the raw value
// ---------------------------------------------------------------------------
// Uses Web Crypto (SHA-256). Requires a secure context, which both
// production (HTTPS) and the CRA dev server (localhost) satisfy.
// Returns null on any failure, which the caller treats as anonymous.
async function hashIdentifier(value) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(String(value));
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (err) {
    return null;
  }
}

// Extracts the identifier to hash from the user object returned by getMe()
// or passed to onLogin. Field name is assumed pending confirmation of the
// exact API response shape — see accompanying note.
function extractRawIdentifier(user) {
  return user?.id ?? user?.userId ?? user?.user_id ?? null;
}

// ---------------------------------------------------------------------------
// GTM virtual page view tracker
// ---------------------------------------------------------------------------
// Fires on every client-side route change, and also when authentication
// status changes at a stable path, so the pageview carries the correct tag.
// Never includes name, email, or any raw account identifier.
function GTMRouteTracker({ hashedUserId }) {
  const location = useLocation();
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtual_page_view',
      page_path: location.pathname,
      user_status: hashedUserId ? 'authenticated' : 'anonymous',
      ...(hashedUserId ? { hashed_user_id: hashedUserId } : {}),
    });
  }, [location.pathname, hashedUserId]);
  return null;
}

// ---------------------------------------------------------------------------
// Breakpoint hook
// ---------------------------------------------------------------------------
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ---------------------------------------------------------------------------
// Nav item helper
// ---------------------------------------------------------------------------
function NavItem({ to, label, icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 18px',
        borderRadius: 6,
        textDecoration: 'none',
        fontWeight: isActive ? 700 : 500,
        fontSize: 14,
        color: isActive ? '#b8860b' : '#c8d6e8',
        background: isActive ? 'rgba(184,134,11,0.12)' : 'transparent',
        borderLeft: isActive ? '3px solid #b8860b' : '3px solid transparent',
        transition: 'all 0.15s',
        marginBottom: 2,
      })}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>{icon}</span>
      {label}
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// Notification bell
// ---------------------------------------------------------------------------
function NotificationBell() {
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [open,          setOpen]          = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const bellRef   = useRef(null);
  const panelRef  = useRef(null);
  const navigate  = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const data = await getNotificationCount();
      setUnreadCount(data?.unread_count ?? 0);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    function handleClick(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current  && !bellRef.current.contains(e.target)
      ) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function handleOpen() {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const data = await getNotifications();
        setNotifications(data?.notifications ?? []);
        fetchCount();
      } catch (_) {}
      setLoading(false);
    }
  }

  async function handleDismiss(e, id) {
    e.stopPropagation();
    try {
      await dismissNotification(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, dismissed_at: new Date().toISOString(), is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (_) {}
  }

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  }

  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null;

  function getPanelStyle() {
    if (!bellRef.current) return { display: 'none' };
    const rect = bellRef.current.getBoundingClientRect();
    const panelWidth = Math.min(360, window.innerWidth - 16);
    let left = rect.left;
    if (left + panelWidth > window.innerWidth - 8) left = window.innerWidth - panelWidth - 8;
    return {
      position: 'fixed',
      bottom: window.innerHeight - rect.top + 6,
      left: Math.max(8, left),
      width: panelWidth,
      maxHeight: '70vh',
      overflowY: 'auto',
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
      zIndex: 3000,
    };
  }

  return (
    <>
      <button ref={bellRef} onClick={handleOpen} title="Notifications" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#c8d6e8', fontSize: 13, fontWeight: 500, padding: '8px 12px', cursor: 'pointer', marginBottom: 10, position: 'relative' }}>
        <span style={{ fontSize: 16, lineHeight: 1, position: 'relative' }}>
          🔔
          {badgeLabel && (
            <span style={{ position: 'absolute', top: -6, right: -8, background: '#e53e3e', color: '#fff', borderRadius: 10, fontSize: 9, fontWeight: 700, padding: '1px 4px', lineHeight: 1.4, minWidth: 16, textAlign: 'center', pointerEvents: 'none' }}>
              {badgeLabel}
            </span>
          )}
        </span>
        Notifications
      </button>

      {open && (
        <div ref={panelRef} style={getPanelStyle()}>
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#132e50' }}>Notifications</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {unreadCount > 0 && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{unreadCount} unread</span>}
              {unreadCount > 0 && <button onClick={handleMarkAllRead} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', color: '#6b7280', fontSize: 10, fontWeight: 600, padding: '2px 8px' }}>Mark all read</button>}
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}>×</button>
            </div>
          </div>
          {loading && <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>}
          {!loading && notifications.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No notifications yet.</div>}
          {!loading && notifications.map(n => {
            const isDismissed = Boolean(n.dismissed_at);
            const isRead = n.is_read;
            return (
              <div key={n.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', background: isDismissed ? '#fafafa' : isRead ? '#fff' : '#fffbf0', opacity: isDismissed ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: isRead ? 500 : 700, color: '#132e50', marginBottom: 3, lineHeight: 1.4 }}>{n.title}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                      {n.type === 'clause_updated' ? (
                        <span onClick={() => { setOpen(false); navigate('/regulatory-library'); }} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#4b6cb7' }}>{n.body}</span>
                      ) : n.body}
                    </div>
                    <div style={{ fontSize: 10, color: '#d1d5db', marginTop: 4 }}>
                      {new Date(n.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {isDismissed && <span style={{ marginLeft: 6 }}>Dismissed</span>}
                    </div>
                  </div>
                  {!isDismissed && <button onClick={(e) => handleDismiss(e, n.id)} title="Dismiss" style={{ flexShrink: 0, background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 14, padding: '2px 4px', borderRadius: 4, lineHeight: 1 }}>×</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Platform alert banner (platform owner only)
// ---------------------------------------------------------------------------
function PlatformAlertBanner({ user }) {
  const [alertCount, setAlertCount] = useState(0);
  const [dismissed,  setDismissed]  = useState(false);
  const isPlatformOwner = user?.org_id === process.env.REACT_APP_PLATFORM_OWNER_ORG_ID;

  useEffect(() => {
    if (!isPlatformOwner) return;
    getRegulatoryAlerts().then(data => {
      setAlertCount((data?.alerts ?? []).filter(a => a.status === 'new').length);
    }).catch(() => {});
  }, [isPlatformOwner]);

  if (!isPlatformOwner || alertCount === 0 || dismissed) return null;

  return (
    <div style={{ background: '#7c3aed', color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, gap: 12, flexShrink: 0 }}>
      <span>🔍 {alertCount} new regulatory source {alertCount === 1 ? 'change' : 'changes'} detected. Review in <a href="/regulatory-alerts" style={{ color: '#e9d5ff', textDecoration: 'underline' }}>Regulatory Alerts</a>.</span>
      <button onClick={() => setDismissed(true)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, color: '#fff', fontSize: 11, padding: '3px 10px', cursor: 'pointer', flexShrink: 0 }}>Dismiss</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client notification banner
// ---------------------------------------------------------------------------
function ClientNotificationBanner({ user }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissed,   setDismissed]   = useState(false);
  const isPlatformOwner = user?.org_id === process.env.REACT_APP_PLATFORM_OWNER_ORG_ID;

  useEffect(() => {
    if (isPlatformOwner) return;
    getNotificationCount().then(data => setUnreadCount(data?.unread_count ?? 0)).catch(() => {});
  }, [isPlatformOwner]);

  if (isPlatformOwner || unreadCount === 0 || dismissed) return null;

  return (
    <div style={{ background: '#132e50', color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, gap: 12, flexShrink: 0 }}>
      <span>📋 {unreadCount} regulatory {unreadCount === 1 ? 'update requires' : 'updates require'} your review. Check the <a href="/regulatory-library" style={{ color: '#b8d4f0', textDecoration: 'underline' }}>Regulatory Library</a>.</span>
      <button onClick={() => setDismissed(true)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, color: '#fff', fontSize: 11, padding: '3px 10px', cursor: 'pointer', flexShrink: 0 }}>Dismiss</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trial expired wall — full screen, blocks all navigation
// ---------------------------------------------------------------------------
function TrialExpiredWall({ user, onLogout }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#f5f2ed', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '48px 40px', maxWidth: 520, width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 16 }}>
          Flow Advisory Group · AI Governance Platform
        </div>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#132e50', marginBottom: 12 }}>
          Your trial has ended
        </div>
        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 12, lineHeight: 1.6 }}>
          Hi {user?.name} — your 14-day trial for <strong>{user?.org_name}</strong> has expired.
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 32, lineHeight: 1.6 }}>
          Your governance data is retained and will be restored immediately when you upgrade.
        </div>
        <button
          onClick={() => navigate('/upgrade')}
          style={{ background: '#b8860b', color: '#fff', border: 'none', borderRadius: 6, padding: '13px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 16, width: '100%' }}
        >
          Renew access — view plans
        </button>
        <div>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar content
// ---------------------------------------------------------------------------
function SidebarContent({ user, onLogout, onNavClick }) {
  const isPlatformOwner = user?.org_id === process.env.REACT_APP_PLATFORM_OWNER_ORG_ID;
  const isAdmin         = user?.role === 'admin';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '24px 18px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase' }}>Flow Advisory Group</div>
        <div style={{ fontSize: 11, color: '#6b85a3', marginTop: 2 }}>AI Governance Platform</div>
      </div>

      <nav style={{ padding: '16px 10px', flex: 1, overflowY: 'auto' }}>
        <NavItem to="/dashboard" label="Dashboard" icon="⊞" onClick={onNavClick} />

        <div style={{ margin: '16px 8px 8px', fontSize: 10, fontWeight: 700, color: '#4a6280', letterSpacing: 0.8, textTransform: 'uppercase' }}>Modules</div>
        <NavItem to="/vendors"           label="AI Vendor Register" icon="⬡" onClick={onNavClick} />
        <NavItem to="/controls"          label="Control Evidence"   icon="⊟" onClick={onNavClick} />
        <NavItem to="/incidents"         label="Incident Register"  icon="⚠" onClick={onNavClick} />
        <NavItem to="/board-packs"       label="Board Pack"         icon="⊕" onClick={onNavClick} />

        <div style={{ margin: '16px 8px 8px', fontSize: 10, fontWeight: 700, color: '#4a6280', letterSpacing: 0.8, textTransform: 'uppercase' }}>Reference</div>
        <NavItem to="/regulatory-library" label="Regulatory Library" icon="§" onClick={onNavClick} />

        {isAdmin && (
          <>
            <div style={{ margin: '16px 8px 8px', fontSize: 10, fontWeight: 700, color: '#4a6280', letterSpacing: 0.8, textTransform: 'uppercase' }}>Admin</div>
            <NavItem to="/users" label="User Management" icon="👥" onClick={onNavClick} />
          </>
        )}

        {isPlatformOwner && (
          <>
            <div style={{ margin: '16px 8px 8px', fontSize: 10, fontWeight: 700, color: '#4a6280', letterSpacing: 0.8, textTransform: 'uppercase' }}>Platform Admin</div>
            <NavItem to="/regulatory-alerts" label="Regulatory Alerts" icon="🔍" onClick={onNavClick} />
          </>
        )}
      </nav>

      <div style={{ padding: '12px 10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <NotificationBell />
        <div style={{ padding: '4px 8px 0', fontSize: 12 }}>
          <div style={{ color: '#c8d6e8', fontWeight: 600, marginBottom: 2 }}>{user?.name || 'User'}</div>
          <div style={{ color: '#4a6280', marginBottom: 6, textTransform: 'capitalize' }}>{user?.role?.replace('_', ' ') || ''}</div>
          <NavLink to="/profile" onClick={onNavClick} style={{ display: 'block', fontSize: 11, color: '#6b85a3', marginBottom: 10, textDecoration: 'none' }}>
            Edit profile
          </NavLink>
          <button onClick={onLogout} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 5, color: '#6b85a3', fontSize: 11, fontWeight: 600, padding: '5px 12px', cursor: 'pointer', width: '100%' }}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App shell
// ---------------------------------------------------------------------------
function AppShell({ user, onLogout, children }) {
  const isMobile                    = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  function closeDrawer() { setDrawerOpen(false); }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f2ed' }}>
      {!isMobile && (
        <aside style={{ width: 240, background: '#132e50', position: 'fixed', top: 0, left: 0, bottom: 0, overflow: 'visible', flexShrink: 0, zIndex: 100 }}>
          <SidebarContent user={user} onLogout={onLogout} onNavClick={undefined} />
        </aside>
      )}

      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 52, background: '#132e50', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', zIndex: 200 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase' }}>Flow Advisory Group</div>
            <div style={{ fontSize: 10, color: '#6b85a3', marginTop: 1 }}>AI Governance Platform</div>
          </div>
          <button onClick={() => setDrawerOpen(o => !o)} aria-label="Toggle menu" style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: '#c8d6e8', fontSize: 20, width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {drawerOpen ? '✕' : '☰'}
          </button>
        </div>
      )}

      {isMobile && drawerOpen && <div onClick={closeDrawer} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 150 }} />}

      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 260, background: '#132e50', zIndex: 160, transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.22s ease', overflow: 'visible' }}>
          <SidebarContent user={user} onLogout={onLogout} onNavClick={closeDrawer} />
        </div>
      )}

      <main style={{ marginLeft: isMobile ? 0 : 240, marginTop: isMobile ? 52 : 0, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: isMobile ? 'calc(100vh - 52px)' : '100vh' }}>
        <PlatformAlertBanner      user={user} />
        <ClientNotificationBanner user={user} />
        <div style={{ flex: 1 }}>{children}</div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trial expiry check — used in ProtectedRoutes
// ---------------------------------------------------------------------------
function isTrialExpired(user) {
  if (!user) return false;
  if (!user.trial_ends_at) return false;
  if (user.subscription_tier && user.subscription_tier !== 'pilot') return false;
  return new Date(user.trial_ends_at) < new Date();
}

// ---------------------------------------------------------------------------
// Root app
// ---------------------------------------------------------------------------
export default function App() {
  const [user,          setUser]          = useState(null);
  const [loading,        setLoading]      = useState(true);
  const [hashedUserId,  setHashedUserId]  = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    getMe()
      .then(async (u) => {
        const rawId = extractRawIdentifier(u);
        if (rawId) {
          const hashed = await hashIdentifier(rawId);
          setHashedUserId(hashed);
        }
        setUser(u);
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);

  function handleLogout() {
    localStorage.removeItem('token');
    setUser(null);
    setHashedUserId(null);
  }

  async function handleLogin(u) {
    const rawId = extractRawIdentifier(u);
    if (rawId) {
      const hashed = await hashIdentifier(rawId);
      setHashedUserId(hashed);
    } else {
      setHashedUserId(null);
    }
    setUser(u);
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f2ed', color: '#132e50', fontSize: 16 }}>
        Loading…
      </div>
    );
  }

  return (
    <Router>
      <GTMRouteTracker hashedUserId={hashedUserId} />
      <Routes>
        {/* Public routes */}
        <Route path="/login"          element={user ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />} />
        <Route path="/register"       element={user ? <Navigate to="/dashboard" replace /> : <Register onLogin={handleLogin} />} />
        <Route path="/invite/accept"  element={user ? <Navigate to="/dashboard" replace /> : <InviteAccept onLogin={handleLogin} />} />

        {/* Upgrade routes — accessible while authenticated (trial expired users land here) */}
        <Route path="/upgrade"         element={!user ? <Navigate to="/login" replace /> : <Upgrade user={user} />} />
        <Route path="/upgrade/success" element={!user ? <Navigate to="/login" replace /> : <Upgrade user={user} />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            !user ? (
              <Navigate to="/login" replace />
            ) : isTrialExpired(user) ? (
              // Trial expired — show wall, allow /upgrade only.
              // Shares the outer Router's history; no nested BrowserRouter.
              <Routes>
                <Route path="/upgrade"         element={<Upgrade user={user} />} />
                <Route path="/upgrade/success" element={<Upgrade user={user} />} />
                <Route path="*"                element={<TrialExpiredWall user={user} onLogout={handleLogout} />} />
              </Routes>
            ) : (
              <AppShell user={user} onLogout={handleLogout}>
                <Routes>
                  <Route path="/dashboard"          element={<Dashboard         user={user} />} />
                  <Route path="/vendors"            element={<Vendors           user={user} />} />
                  <Route path="/controls"           element={<Controls          user={user} />} />
                  <Route path="/incidents"          element={<Incidents         user={user} />} />
                  <Route path="/board-packs"        element={<BoardPacks        user={user} />} />
                  <Route path="/regulatory-library" element={<RegulatoryLibrary user={user} />} />
                  <Route path="/regulatory-alerts"  element={<RegulatoryAlerts  user={user} />} />
                  <Route path="/users"              element={<UserManagement    user={user} />} />
                  <Route path="/profile"            element={<Profile           user={user} onUserUpdate={u => setUser(prev => ({...prev, ...u}))} />} />
                  <Route path="*"                   element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppShell>
            )
          }
        />
      </Routes>
    </Router>
  );
}